import React, { useState, useEffect } from 'react';
import { Pencil, Check, X, RefreshCw, AlertCircle, History } from 'lucide-react';
import { colors } from '../../constants/theme';
import { matchesService } from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';

// Rótulos curtos, para o chip ao lado do id da partida.
const PHASE_LABELS = {
  grupos: 'Grupos',
  quartas: 'Quartas',
  semifinal: 'Semifinal',
  final: 'Final',
  terceiro: '3º Lugar',
};

export default function ResultsEditor() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editGoals, setEditGoals] = useState({ g1: 0, g2: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await matchesService.getHistory({ limit: 100 });
      if (resp.success) {
        setMatches(resp.data.matches || []);
      }
    } catch (e) {
      setError(e.error?.message || 'Erro ao carregar histórico de partidas.');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (match) => {
    setEditingId(match.id);
    setEditGoals({
      g1: match.finalScore?.player1 ?? 0,
      g2: match.finalScore?.player2 ?? 0,
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError(null);
  };

  const handleSave = async (match) => {
    const g1 = parseInt(editGoals.g1, 10);
    const g2 = parseInt(editGoals.g2, 10);
    if (Number.isNaN(g1) || Number.isNaN(g2) || g1 < 0 || g2 < 0) {
      setError('Placar inválido.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const resp = await matchesService.editResult(match.id, g1, g2);
      if (resp.success) {
        setEditingId(null);
        await loadHistory();
      }
    } catch (e) {
      setError(e.error?.message || 'Erro ao editar resultado.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner size="medium" text="Carregando partidas..." />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
          Editar Resultados
        </h2>
        <button
          onClick={loadHistory}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-2">
        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Corrija o placar de partidas já finalizadas. A tabela é recalculada automaticamente.
          A edição é bloqueada se a fase seguinte que depende do resultado já tiver começado
          (ex: não dá pra editar a semifinal se a final já começou).
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {matches.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Nenhuma partida finalizada ainda.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {matches.map((match) => {
            const editing = editingId === match.id;
            const winnerName =
              match.winnerId === match.player1?.id
                ? match.player1?.name
                : match.winnerId === match.player2?.id
                ? match.player2?.name
                : null;
            return (
              <div key={match.id} className="p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                      {PHASE_LABELS[match.phase] || match.phase}
                    </span>
                    <span className="text-xs text-gray-400">#{match.id}</span>
                  </div>
                  {match.finishedAt && (
                    <span className="text-xs text-gray-400">
                      {new Date(match.finishedAt).toLocaleString('pt-BR')}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between gap-4">
                  <div className="flex-1 text-right font-medium">{match.player1?.name}</div>

                  {editing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={editGoals.g1}
                        onChange={(e) => setEditGoals((p) => ({ ...p, g1: e.target.value }))}
                        className="w-14 px-2 py-1 border rounded text-center"
                      />
                      <span className="text-gray-400">x</span>
                      <input
                        type="number"
                        min="0"
                        value={editGoals.g2}
                        onChange={(e) => setEditGoals((p) => ({ ...p, g2: e.target.value }))}
                        className="w-14 px-2 py-1 border rounded text-center"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-2xl font-bold" style={{ color: colors.primary }}>
                      <span>{match.finalScore?.player1 ?? 0}</span>
                      <span className="text-gray-300 text-lg">x</span>
                      <span>{match.finalScore?.player2 ?? 0}</span>
                    </div>
                  )}

                  <div className="flex-1 font-medium">{match.player2?.name}</div>

                  <div className="flex items-center gap-2">
                    {editing ? (
                      <>
                        <button
                          onClick={() => handleSave(match)}
                          disabled={saving}
                          className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded disabled:opacity-50"
                          title="Salvar"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded disabled:opacity-50"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEdit(match)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                      >
                        <Pencil className="h-4 w-4" />
                        Editar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
